import React from 'react';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import hljs from 'highlight.js';

// Create a custom component to handle mermaid code blocks
const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match && match[1] ? match[1] : '';
  
  // Special handling for mermaid code blocks
  if (language === 'mermaid') {
    return (
      <div className="mermaid-diagram">
        <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-auto">
          <code className="text-sm">{children}</code>
        </pre>
        <div className="text-xs text-gray-500 mt-1">
          Mermaid diagram (view source)
        </div>
      </div>
    );
  }
  
  // For all other languages, use default rendering
  return !inline ? (
    <pre className={className} {...props}>
      <code className={className} {...props}>
        {children}
      </code>
    </pre>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

interface TextArtifactViewerProps {
  artifact: Artifact;
}

export const TextArtifactViewer: React.FC<TextArtifactViewerProps> = ({ artifact }) => {
  const { darkMode } = useTheme();
  
  return (
    <div className="text-artifact-viewer">
      <div className={`prose prose-sm max-w-none ${darkMode ? 'prose-invert' : ''}`}>
        {typeof artifact.content === 'string' ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[
              // Configure rehype-highlight to ignore mermaid
              [(rehypeHighlight as any), { 
                ignoreMissing: true, 
                subset: false // Don't throw on unknown languages
              }]
            ]}
            components={{
              code: CodeBlock // Use our custom code block component
            }}
          >
            {artifact.content}
          </ReactMarkdown>
        ) : (
          <pre className={`whitespace-pre-wrap text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            {JSON.stringify(artifact.content, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};
